<template>
  <div class="splash-page">
    <div class="c-responsive-fixwidth">
      <div class="o-main-content-area">
        <div class="col-md-12 o-no-padding c-common-box-shadow u-bg-color-fff" style="padding: 20px !important;">

          <div class="row" style="margin-bottom: 15px;">
            <div class="col-md-6">
              <h3>Login Page Splash Message</h3>
            </div>
            <div class="col-md-6 text-right">
              <!-- #1686 — editing the login/splash message writes client config; CA/SA only ('config' power).
                   #1741 — plus Power Users a CA has designated in this client's `splash-editors` setting.
                   That list is what the selector below writes and what sideNav uses to show this page at all,
                   so a designated editor who can reach the page can now also save it. The grant is per-user,
                   made by a CA, and the backend scopes it to `login_page_message` alone. -->
              <button v-if="canEditSplash" class="c-solid-btn c-solid-btn--green" @click="saveMessage" :disabled="saving">
                <i class="fa fa-floppy-o" aria-hidden="true"></i> Save
              </button>
            </div>
          </div>

          <!-- Power User Access (client-admin only) -->
          <div v-if="userRoleSlug === 'client-admin'" class="row" style="margin-bottom: 20px;">
            <div class="col-md-12">
              <label style="margin-bottom: 8px; display: block;">Power Users allowed to edit this page:</label>
              <el-select
                v-model="selectedEditors"
                multiple
                filterable
                placeholder="Select power users..."
                style="width: 100%;"
                @change="saveEditors"
              >
                <el-option
                  v-for="pu in powerUsers"
                  :key="pu.id"
                  :label="pu.first_name + ' ' + pu.last_name"
                  :value="pu.id"
                ></el-option>
              </el-select>
            </div>
          </div>

          <!-- CKEditor -->
          <div class="row">
            <div class="col-md-12">
              <textarea id="splash-editor" name="splash-editor" style="width: 100%; min-height: 300px;"></textarea>
              <!-- #2036 — the live counter and the live verdict. The warning is the SAME
                   sentence the server would refuse with, so the person is never surprised by
                   a save. -->
              <div class="o-splash-editor-status">
                <span :class="{ 'err-msg': messageMax && messageLength > messageMax }">
                  {{ messageLength }}<span v-if="messageMax">/{{ messageMax }}</span>
                </span>
                <span v-if="messageWarning" class="err-msg o-splash-editor-warning">{{ messageWarning }}</span>
              </div>
            </div>
          </div>

          <!-- Preview -->
          <div class="row" style="margin-top: 20px;">
            <div class="col-md-12">
              <h5 style="margin-bottom: 10px;">Preview (as seen on login page):</h5>
              <div class="splash-preview" v-html="previewHtml"></div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import service from '../services.js';
// #1686 — shared role-capability check (source of getPower('config') across the app)
import dateTimeConvert from '../timeConvert&Compare';
import { sanitize, describeProblems } from '../safeHtmlRules.js';   // #2036
import { editorConfig, constrain, refusalFor } from '../safeHtmlEditor.js';   // #2036

export default {
  name: 'SplashPage',
  data() {
    return {
      client: null,
      powerUsers: [],
      selectedEditors: [],
      // #1741 — the client's `splash-editors` list, loaded with the client.
      // Kept separate from selectedEditors: that one is the CA's edit buffer.
      splashEditors: [],
      saving: false,
      editorInstance: null,
      previewHtml: '',
      // #2036 — the live character counter and the live verdict shown under the editor.
      messageLength: 0,
      messageMax: 0,
      messageWarning: ''
    };
  },
  computed: {
    ...mapGetters({
      userToken: 'userToken',
      userRole: 'userRole',
      userClientID: 'userClientID',
      employeeId: 'employeeId',
      userRoleSlug: 'userRoleSlug'
    }),
    /**
     * #1741 — is the current user a Power User this CA designated as a splash editor?
     * Matches sideNav.vue:561, which decides whether the Splash Page menu item appears
     * at all: the list holds EMPLOYEE ids, compared against `employeeId`.
     */
    isSplashEditor() {
      return this.splashEditors.indexOf(this.employeeId) !== -1;
    },
    /**
     * The gate for the Save button. `config` is CA/SA (checkAccess removes it from
     * power-user); the splash-editors list is the deliberate per-user exception.
     * The button being visible is a convenience — `ownerOptions` enforces the same
     * rule server-side and is the thing that actually decides.
     */
    canEditSplash() {
      return this.getPower('config') || this.isSplashEditor;
    }
  },
  methods: {
    // #1686 — same delegate the rest of the app uses for getPower('config'): CA/SA → true.
    getPower(data) {
      return dateTimeConvert.checkAccess(data);
    },
    async loadClient() {
      try {
        var res = await service.get('client/' + this.userClientID, {
          'Authorization': this.userToken
        });
        this.client = res.data.result;
        var msg = this.client.login_page_message || '';
        // #2036 — a row stored before #2036 is unvalidated; the preview must not run it either.
        this.previewHtml = sanitize(msg);

        // Load saved editors from settings
        if (this.client.settings && this.client.settings['splash-editors']) {
          this.selectedEditors = this.client.settings['splash-editors'];
          // #1741 — same list, held separately so the CA editing the selector
          // does not change who is allowed to save until it is persisted.
          this.splashEditors = this.client.settings['splash-editors'].slice();
        }

        // Set editor content after CKEditor is ready
        var self = this;
        if (self.editorInstance) {
          self.editorInstance.setData(msg);
        } else {
          // Wait for editor init
          setTimeout(function () {
            if (self.editorInstance) {
              self.editorInstance.setData(msg);
            }
          }, 2500);
        }
      } catch (err) {
        console.log('loadClient error:', err);
      }
    },

    async loadPowerUsers() {
      try {
        var res = await service.get('employee/getPowerUsers', {
          'Authorization': this.userToken,
          'cs3-context-role': this.userRole,
          'cs3-context-clientId': this.userClientID
        });
        this.powerUsers = res.data.result || [];
      } catch (err) {
        console.log('loadPowerUsers error:', err);
      }
    },

    async saveMessage() {
      if (!this.editorInstance) return;
      // #1686 — the login/splash message is client-wide config: CA/SA, or (#1741) a Power
      // User this client's CA designated as a splash editor. The backend enforces the same
      // rule and scopes the designated-editor path to `login_page_message` alone, so this
      // is a UX guard, not the security boundary. FS #16334 was an undesignated Employee
      // editing this greeting; that stays closed — an unlisted user still 403s.
      if (!this.canEditSplash) {
        this.$notify.error({ title: 'Access denied', message: 'Only a Client Admin or a designated splash editor can edit the login message.' });
        return;
      }
      var self = this;
      self.saving = true;
      var html = self.editorInstance.getData();

      // #2036 — refuse before the round trip, with the same verdict the server will reach.
      // The server check is the one that counts; this exists so the person is told at once.
      var refusal = refusalFor(html);
      if (refusal) {
        self.saving = false;
        self.$notify.error({ title: 'Cannot save', message: refusal, duration: 10000 });
        return;
      }

      try {
        // Save via ownerOptions (same endpoint used by configOwnerOption)
        await service.post('employee/ownerOptions', {
          client_id: self.userClientID,
          login_page_message: html
        }, {
          'Authorization': self.userToken
        });

        self.previewHtml = sanitize(html);
        self.$notify.success({
          title: 'Success',
          message: 'Splash message saved'
        });
      } catch (err) {
        console.log('saveMessage error:', err);
        self.$notify.error({
          title: 'Error',
          message: 'Failed to save splash message'
        });
      }
      self.saving = false;
    },

    async saveEditors() {
      try {
        // Only a CA reaches this (the selector renders for client-admin only) and the
        // backend re-checks — see #1743: saveClientSettings was ungated until this MR.

        await service.post('client/' + this.userClientID + '/saveClientSettings', {
          key: 'splash-editors',
          value: this.selectedEditors
        }, {
          'Authorization': this.userToken,
          'cs3-context-clientId': this.userClientID,
          'cs3-context-role': this.userRole
        });
        this.$notify.success({
          title: 'Success',
          message: 'Editor access updated'
        });
      } catch (err) {
        console.log('saveEditors error:', err);
        this.$notify.error({
          title: 'Error',
          message: 'Failed to update editor access'
        });
      }
    },

    initEditor() {
      var self = this;
      setTimeout(function () {
        // #2036 — the toolbar, the allow-list, the paste rule and the link rule all come from
        // src/safeHtmlEditor.js, which derives them from the same three lists the server
        // validates against (src/safeHtmlRules.js <-> app/Support/SafeHtml.php). Configuring
        // them here as well is how the two editors of this field would drift.
        self.editorInstance = CKEDITOR.replace('splash-editor', editorConfig({
          height: '45vh',
          contentsCss: [CKEDITOR.basePath + 'contents.css', 'body { color: #ffffff; background: #335479; } a { color: #f1c40f !important; }'],
          on: {
            instanceReady: function (evt) {
              // Default links to new window
              evt.editor.on('dialogShow', function (e) {
                var dialog = e.data;
                if (dialog.getName() === 'link') {
                  var targetTab = dialog.getContentElement('target', 'linkTargetType');
                  if (targetTab && !dialog.getValueOf('target', 'linkTargetType')) {
                    dialog.setValueOf('target', 'linkTargetType', '_blank');
                  }
                }
              });

              // Update preview on changes. #2036 — the preview goes through the SAME
              // sanitizer as the login page, so what the CA sees is what a visitor will get.
              // Before this it previewed the raw editor output, which is a WYSIWYG that lies.
              evt.editor.on('change', function () {
                self.previewHtml = sanitize(evt.editor.getData());
              });

              // Load content if client already loaded
              if (self.client && self.client.login_page_message) {
                evt.editor.setData(self.client.login_page_message);
              }

              // #2036 — paste stripping with a named message, the link nudge, and the live
              // verdict + counter.
              constrain(evt.editor, {
                onRemoved: function (message) {
                  self.$notify.warning({ title: 'Pasted content trimmed', message: message, duration: 8000 });
                },
                onProblems: function (problems, length, max) {
                  self.messageLength = length;
                  self.messageMax = max;
                  self.messageWarning = problems.length ? describeProblems(problems) : '';
                }
              });
            }
          }
        }));
      }, 500);
    }
  },

  async created() {
    await this.loadClient();
    if (this.userRoleSlug === 'client-admin') {
      await this.loadPowerUsers();
    }
  },

  mounted() {
    this.initEditor();
  },

  beforeDestroy() {
    if (this.editorInstance) {
      try { this.editorInstance.destroy(); } catch (e) { /* noop */ }
    }
  }
};
</script>

<style scoped>
/* #2036 */
.o-splash-editor-status { display: flex; gap: 12px; align-items: baseline; margin-top: 6px; font-size: 12px; }
.o-splash-editor-warning { flex: 1; }
</style>
<style scoped>
.splash-preview {
  background: #335479;
  color: #fff;
  border-radius: 4px;
  padding: 20px;
  min-height: 80px;
  border: 1px solid #466D98;
}
.splash-preview a {
  color: #f1c40f;
}
</style>
